import React, { useMemo, useState, useEffect } from "react";
import Utils from "../../utility";
import { getImageTag } from "../../services/zbrainConnectorService";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import moment from "moment";

const DAMAGE_REQUIRES_LOCATION = ["Object", "Hole", "Cut", "Gouge", "Bubble"];
const DAMAGE_REQUIRES_FAILURE = ["Hole", "Cut", "Gouge"];

const ImageZoom = ({
  openImage,
  setImagePopup,
  data,
  claimData,
  claimModel = "claimrecords",
  localData,
  setLocalData
}) => {
  const [scale, setScale] = useState(1);
  const [index, setIndex] = useState(openImage?.idx || 0);

  // v3 tagging state (schema-aligned)
  const [imageEligibility, setImageEligibility] = useState("");
  const [damageType, setDamageType] = useState("");
  const [location, setLocation] = useState("");
  const [objectCharacteristics, setObjectCharacteristics] = useState([]);
  const [cordVisible, setCordVisible] = useState("");
  const [leakTest, setLeakTest] = useState("");
  const [leakTestResult, setLeakTestResult] = useState("");
  const [treadDepthLocation, setTreadDepthLocation] = useState("");
  const [tireWorn, setTireWorn] = useState(false);
  const [inspectionTags, setInspectionTags] = useState([]);
  const [imageQuality, setImageQuality] = useState("");
  const [validationMsg, setValidationMsg] = useState("");

  const user = useSelector((state) => state.user);
  const [edit, setEdit] = useState(false);

  const zoomIn = () => setScale(scale + 0.1);
  const zoomOut = () => setScale(scale > 1 ? scale - 0.1 : scale);

  const closePreviewModal = () => {
    setScale(1);
    setImagePopup((prev) => ({ ...prev, popup: false }));
  };

  const tireComponent = {
    "RIGHT FRONT TIRE": "rightFront",
    "RIGHT REAR TIRE": "rightRear",
    "LEFT FRONT TIRE": "leftFront",
    "LEFT REAR TIRE": "leftRear"
  };

  const componentName = openImage?.allImages[index]?.componentName;
  const componentData = data[componentName] || [];

  const currentImage = openImage?.allImages?.[index] || {};
  const currentImageId =
    currentImage?.imageId || currentImage?.documentId || currentImage?.id;

  const isPnp = imageEligibility === "pnp";
  const isEligible = imageEligibility === "yes";
  const isVoidDamage = damageType === "Void";
  const showLocation = DAMAGE_REQUIRES_LOCATION.includes(damageType);
  const showFailure = DAMAGE_REQUIRES_FAILURE.includes(damageType);
  const showObject = damageType === "Object";
  const showTreadAndInspection = isEligible && !!damageType && !isVoidDamage;

  const toggleMulti = (arr, value) =>
    arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];

  const airLossConfirmed = useMemo(() => {
    if (!imageEligibility || isPnp || !damageType) return undefined;
    return cordVisible === "yes" ||
      (leakTest === "yes" && leakTestResult === "positive") ||
      damageType === "Object"
      ? "Air Loss Confirmed"
      : "No Air Loss Confirmed";
  }, [
    imageEligibility,
    isPnp,
    damageType,
    cordVisible,
    leakTest,
    leakTestResult
  ]);

  const buildTag = () => {
    if (isPnp) {
      return { imageEligibility: "pnp" };
    }

    const tag = {
      imageEligibility: imageEligibility || undefined,
      damageType: damageType || undefined,
      airLossConfirmed
    };

    if (showLocation) {
      tag.location = location || undefined;
    }

    if (showObject) {
      tag.objectCharacteristics =
        objectCharacteristics.length > 0 ? objectCharacteristics : undefined;
    }

    if (showFailure) {
      tag.cordVisible = cordVisible || undefined;
      tag.leakTest = leakTest || undefined;
      tag.leakTestResult = leakTestResult || undefined;
    }

    if (showTreadAndInspection) {
      tag.inspectionTags =
        inspectionTags.length > 0 ? inspectionTags : undefined;
      tag.tireCondition = {
        treadDepthLocation: treadDepthLocation || undefined,
        tireWorn: tireWorn ? "yes" : "no"
      };
    }

    if (isEligible) {
      tag.imageQuality = imageQuality || undefined;
    }

    return tag;
  };

  const normalizeForCompare = (t = {}) => {
    const tc = t?.tireCondition || {};
    return JSON.stringify({
      imageEligibility: t?.imageEligibility || undefined,
      damageType: t?.damageType || undefined,
      location: t?.location || undefined,
      objectCharacteristics: Array.isArray(t?.objectCharacteristics)
        ? [...t.objectCharacteristics].sort()
        : undefined,
      cordVisible: t?.cordVisible || undefined,
      leakTest: t?.leakTest || undefined,
      leakTestResult: t?.leakTestResult || undefined,
      tireCondition:
        tc?.treadDepthLocation || tc?.tireWorn
          ? {
              treadDepthLocation: tc?.treadDepthLocation,
              tireWorn: tc?.tireWorn
            }
          : undefined,
      inspectionTags: Array.isArray(t?.inspectionTags)
        ? [...t.inspectionTags].sort()
        : undefined,
      imageQuality: t?.imageQuality || undefined,
      airLossConfirmed: t?.airLossConfirmed || undefined
    });
  };

  const clearAllFields = () => {
    setImageEligibility("");
    setDamageType("");
    setLocation("");
    setObjectCharacteristics([]);
    setCordVisible("");
    setLeakTest("");
    setLeakTestResult("");
    setTreadDepthLocation("");
    setTireWorn(false);
    setInspectionTags([]);
    setImageQuality("");
    setValidationMsg("");
  };

  const restoreFromSavedTag = (savedTag = {}) => {
    setImageEligibility(savedTag.imageEligibility || "");
    setDamageType(savedTag.damageType || "");
    setLocation(savedTag.location || "");
    setObjectCharacteristics(savedTag.objectCharacteristics || []);
    setCordVisible(savedTag.cordVisible || "");
    setLeakTest(savedTag.leakTest || "");
    setLeakTestResult(savedTag.leakTestResult || "");
    setTreadDepthLocation(savedTag.tireCondition?.treadDepthLocation || "");
    setTireWorn(savedTag.tireCondition?.tireWorn === "yes");
    setInspectionTags(savedTag.inspectionTags || []);
    setImageQuality(savedTag.imageQuality || "");
    setValidationMsg("");
  };

  const validateForSubmit = () => {
    if (!imageEligibility) return "Please select Image Eligibility.";
    if (isPnp) return "";
    if (!damageType) return "Please select Damage Type.";
    if (showLocation && !location)
      return "Please select Damage Location (Tread or Shoulder/Sidewall).";
    if (showFailure && !cordVisible) return "Please select Cord Visibility.";
    if (showFailure && !leakTest) return "Please select Leak Test Performed.";
    if (showFailure && leakTest === "yes" && !leakTestResult)
      return "Please select Leak Test Result.";
    return "";
  };

  const ImageTagging = async () => {
    try {
      const err = validateForSubmit();
      if (err) {
        setValidationMsg(err);
        return;
      }
      setValidationMsg("");

      const newState = {
        tag: buildTag(),
        component: "tire",
        // schema expects imageId; keep documentId for backward compatibility
        imageId: currentImageId,
        documentId: currentImageId,
        componentLocation: tireComponent[componentName],
        claimId: claimData?._id || claimData?.claimId,
        claimNumber: claimData?.claimNumber,
        claimModel,
        taggedBy: {
          userId: user?.userId,
          name: user?.name,
          email: user?.email
        },
        // optional pass-through if present (some backends require these)
        s3bucket: currentImage?.s3bucket,
        s3key: currentImage?.s3key,
        etag: currentImage?.etag,
        contentType: currentImage?.contentType,
        fileSize: currentImage?.fileSize
      };

      const [error, response] = await Utils.parseResponse(
        getImageTag(newState)
      );

      if (error || !response) {
        if (!error?.response?.data?.success);
        if (error?.response?.status === 500) {
          toast.error("Internal server error");
        } else {
          toast.error(error?.response?.data?.message);
        }
        return;
      }

      toast.success("Image tagging saved successfully");
      setLocalData((prev) => [
        ...prev.filter((item) => item?.imageId !== response?.imageId),
        response
      ]);
      setEdit(false);
    } catch (e) {
      console.error(e);
    }
  };

  const hasLocalData = localData.find(
    (prev) => prev?.imageId === currentImageId
  );
  const tag = currentImage?.tag || hasLocalData?.tag || {};
  const hasExistingTag = Object.keys(tag || {}).length > 0;
  const hasTagChanges =
    normalizeForCompare(buildTag()) !== normalizeForCompare(tag);

  useEffect(() => {
    setEdit(hasLocalData ? false : !currentImage?.tag);
    restoreFromSavedTag(tag);
  }, [currentImageId]);

  return (
    <div className="fixed right-0  top-0 z-50  flex size-full bg-black-0">
      <div className="w-1/4 overflow-y-auto bg-white p-5">
        <div className="flex w-full justify-between">
          <div className="font-InterSemiBold text-ft18-21 text-black-10">
            Inspection Tags
          </div>
          <div className="flex gap-2.5">
            <button
              type="button"
              disabled={!edit}
              onClick={clearAllFields}
              className={`rounded border px-2 py-1 font-InterMedium text-ft12-16 ${
                !edit
                  ? "cursor-not-allowed border-grey-70 text-grey-50"
                  : "border-grey-70 text-black-10 hover:bg-grey-90"
              }`}
            >
              Clear all
            </button>
            {hasExistingTag && !edit && (
              <img
                onClick={() => setEdit(true)}
                src="/images/edit.svg"
                alt=""
                className="size-3 cursor-pointer"
              />
            )}
            {hasExistingTag && edit && (
              <img
                className="size-3 cursor-pointer"
                src="/images/cross.svg"
                alt="white-cross-icon"
                onClick={() => {
                  restoreFromSavedTag(tag);
                  setEdit(false);
                }}
              />
            )}
          </div>
        </div>

        <div>
          <div className="z-50 mt-5 size-full rounded-md px-2 drop-shadow-sm">
            <div className="rounded-md">
              <div className="mb-4 rounded-md bg-grey-110 px-3 py-2">
                <h3 className="font-InterMedium text-ft14-17 text-black-20">
                  Global Instruction:
                </h3>
                <p className="mt-1 font-InterRegular text-ft12-16 text-black-20">
                  Select only what is visible in this image. Do not infer cause,
                  intent, policy, coverage, or outcome.
                </p>
                <p className="mt-2 font-InterRegular text-ft12-16 text-black-20">
                  This UI captures visual evidence only to train AI models.
                </p>
              </div>

              <div className="mb-4 rounded-md bg-grey-110 px-3 py-2">
                <h3 className="font-InterMedium text-ft14-17 text-black-20">
                  Image Eligibility
                </h3>
                <p className="mt-2 font-InterSemiBold text-ft17-20 text-black-10">
                  Is this a direct photograph of a tire?
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  <label className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10">
                    <input
                      type="radio"
                      name="imageEligibility"
                      className="mt-1"
                      disabled={!edit}
                      checked={imageEligibility === "yes"}
                      onChange={() => {
                        setImageEligibility("yes");
                        setValidationMsg("");
                      }}
                    />
                    Yes – Direct photo of tire
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10">
                    <input
                      type="radio"
                      name="imageEligibility"
                      className="mt-1"
                      disabled={!edit}
                      checked={imageEligibility === "pnp"}
                      onChange={() => {
                        setImageEligibility("pnp");
                        setDamageType("");
                        setLocation("");
                        setObjectCharacteristics([]);
                        setCordVisible("");
                        setLeakTest("");
                        setLeakTestResult("");
                        setTreadDepthLocation("");
                        setTireWorn(false);
                        setInspectionTags([]);
                        setImageQuality("");
                        setValidationMsg("");
                      }}
                    />
                    <span>
                      No – Picture of a picture / screen capture{" "}
                      <span className="font-InterSemiBold">
                        (PnP – Not Allowed)
                      </span>
                    </span>
                  </label>
                </div>
                <p className="mt-3 font-InterRegular text-ft12-16 text-black-20">
                  PnP includes photos of screens, monitors, tablets, phones, or
                  printed photos. Indicators: visible UI elements, screen
                  borders, glare, reflections, pixel grids, or moiré patterns.
                  PnP images may be retained only to train the AI to detect
                  invalid submissions.
                </p>
                {isPnp && (
                  <p className="mt-2 font-InterRegular text-ft14-17 text-red-10">
                    Inspection stopped. Image is not valid.
                  </p>
                )}
              </div>

              {isEligible && (
                <div className="mb-4 rounded-md bg-grey-110 px-3 py-2">
                  <h3 className="font-InterSemiBold text-ft14-17 text-black-20">
                    Damage Type
                  </h3>
                  <div className="mt-3 flex flex-col gap-2">
                    {[
                      {
                        label: "Object (foreign object visibly present)",
                        value: "Object"
                      },
                      {
                        label: "Hole (opening without visible object)",
                        value: "Hole"
                      },
                      { label: "Cut / Slice", value: "Cut" },
                      { label: "Gouge", value: "Gouge" },
                      { label: "Bubble (intact or ruptured)", value: "Bubble" },
                      { label: "No Damage", value: "NoDamage" },
                      {
                        label:
                          "Missing Tire Component(s) – No tire visible in image (void)",
                        value: "Void"
                      }
                    ].map((o) => (
                      <label
                        key={o.value}
                        className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                      >
                        <input
                          type="radio"
                          name="damageType"
                          className="mt-1"
                          disabled={!edit}
                          checked={damageType === o.value}
                          onChange={() => {
                            setDamageType(o.value);
                            setLocation("");
                            setObjectCharacteristics([]);
                            setCordVisible("");
                            setLeakTest("");
                            setLeakTestResult("");
                            setValidationMsg("");
                          }}
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                  <p className="mt-3 font-InterRegular text-ft12-16 text-black-20">
                    Use{" "}
                    <span className="font-InterSemiBold">
                      Missing Tire Component(s)
                    </span>{" "}
                    when the image does not show a tire (different from{" "}
                    <span className="font-InterSemiBold">No Damage</span>, which
                    applies when a tire is visible and shows no damage). Damage
                    Location does not apply when no tire is visible.
                  </p>
                  {isEligible && showLocation && (
                    <div className="mb-4 mt-2 bg-grey-110">
                      <h3 className="font-InterSemiBold text-ft14-17 text-black-20">
                        Damage Location
                      </h3>
                      <p className="mt-1 font-InterRegular text-ft12-16 text-black-20">
                        (Required for Object, Hole, Cut, Gouge, Bubble – Select
                        ONE)
                      </p>
                      <div className="mt-2 flex flex-col gap-2">
                        {[
                          { label: "Tread", value: "Tread" },
                          { label: "Shoulder / Sidewall", value: "Shoulder" }
                        ].map((o) => (
                          <label
                            key={o.value}
                            className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                          >
                            <input
                              type="radio"
                              name="location"
                              disabled={!edit}
                              checked={location === o.value}
                              onChange={() => {
                                setLocation(o.value);
                                setValidationMsg("");
                              }}
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {isEligible && showObject && (
                    <div className="mb-4 bg-grey-110">
                      <h3 className="font-InterSemiBold text-ft14-17 text-black-20">
                        Object Characteristics
                      </h3>
                      <div className="mt-2 flex flex-col gap-2">
                        {[
                          "Normal road wear",
                          "Pristine / Unworn",
                          "Wear is inconsistent with road hazard",
                          "Orientation is inconsistent with tire rotation",
                          "Appears to be placed / sitting on tire surface",
                          "Unable to determine from image"
                        ].map((v) => (
                          <label
                            key={v}
                            className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                          >
                            <input
                              type="checkbox"
                              disabled={!edit}
                              checked={objectCharacteristics.includes(v)}
                              onChange={() =>
                                setObjectCharacteristics((prev) =>
                                  toggleMulti(prev, v)
                                )
                              }
                            />
                            {v}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {isEligible && showFailure && (
                    <div className="mt-4 bg-grey-110">
                      <h3 className="font-InterSemiBold text-ft14-17 text-black-20">
                        Failure Evidence
                      </h3>
                      <p className="mt-1 font-InterRegular text-ft12-16 text-black-20">
                        Required for Hole, Cut, Gouge only — not for Object
                      </p>

                      <h4 className="mt-3 font-InterSemiBold text-ft14-17 text-black-20">
                        Cord Visibility
                      </h4>
                      <div className="mt-2 flex flex-col gap-2">
                        {[
                          {
                            label: "Cords visible (one or more strands)",
                            value: "yes"
                          },
                          { label: "Cords not visible", value: "no" }
                        ].map((o) => (
                          <label
                            key={o.value}
                            className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                          >
                            <input
                              type="radio"
                              name="cordVisible"
                              className="mt-1"
                              disabled={!edit}
                              checked={cordVisible === o.value}
                              onChange={() => {
                                setCordVisible(o.value);
                                setValidationMsg("");
                              }}
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>

                      <h4 className="mt-3 font-InterSemiBold text-ft14-17 text-black-20">
                        Leak Test Performed
                      </h4>
                      <div className="mt-2 flex flex-col gap-2">
                        {[
                          { label: "Leak test performed", value: "yes" },
                          {
                            label: "Leak test not performed / not shown",
                            value: "no"
                          }
                        ].map((o) => (
                          <label
                            key={o.value}
                            className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                          >
                            <input
                              type="radio"
                              name="leakTest"
                              className="mt-1"
                              disabled={!edit}
                              checked={leakTest === o.value}
                              onChange={() => {
                                setLeakTest(o.value);
                                if (o.value !== "yes") setLeakTestResult("");
                                setValidationMsg("");
                              }}
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>

                      {leakTest === "yes" && (
                        <div className="mt-3">
                          <h4 className="font-InterSemiBold text-ft14-17 text-black-10">
                            Leak Test Result
                          </h4>
                          <div className="mt-2 flex flex-col gap-2">
                            {[
                              {
                                label:
                                  "Positive – active bubbles escaping from a specific point",
                                value: "positive"
                              },
                              {
                                label: "Negative – no active bubbling observed",
                                value: "negative"
                              }
                            ].map((o) => (
                              <label
                                key={o.value}
                                className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                              >
                                <input
                                  type="radio"
                                  name="leakTestResult"
                                  className="mt-1"
                                  disabled={!edit}
                                  checked={leakTestResult === o.value}
                                  onChange={() => {
                                    setLeakTestResult(o.value);
                                    setValidationMsg("");
                                  }}
                                />
                                {o.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {showTreadAndInspection && (
                <div className="mb-4 rounded-md bg-grey-110 px-3 py-2">
                  <h3 className="font-InterSemiBold text-ft14-17 text-black-20">
                    Tire Condition – Tread Wear Distribution
                  </h3>
                  <p className="mt-1 font-InterRegular text-ft12-16 text-black-20">
                    Capture where tread depth is lowest based only on visible
                    evidence. Use tread blocks, grooves, and wear bars as
                    reference. Do not infer inflation, alignment, or mechanical
                    cause. If unable to compare confidently, select &quot;Unable
                    to determine&quot;. Do not rely on tread depth gauges shown
                    in photos.
                  </p>

                  <h4 className="mt-4 font-InterSemiBold text-ft14-17 text-black-10">
                    A. Lowest Tread Depth Location
                  </h4>
                  <div className="mt-2 flex flex-col gap-2">
                    {[
                      {
                        label: "Center wear",
                        value: "center"
                      },
                      {
                        label: "Inner edge wear",
                        value: "inner"
                      },
                      {
                        label: "Outer edge wear",
                        value: "outer"
                      },
                      {
                        label: "Inner and outer edge wear",
                        value: "inner and outer"
                      },
                      {
                        label: "Center and outer edge wear",
                        value: "center and outer"
                      },
                      {
                        label: "Center and inner edge wear",
                        value: "center and inner"
                      },
                      {
                        label: "Even wear",
                        value: "uniform"
                      },
                      {
                        label: "Unable to determine from image",
                        value: "unable"
                      }
                    ].map((o) => (
                      <label
                        key={o.value}
                        className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                      >
                        <input
                          type="radio"
                          name="treadDepthLocation"
                          className="mt-1"
                          disabled={!edit}
                          checked={treadDepthLocation === o.value}
                          onChange={() => setTreadDepthLocation(o.value)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>

                  <h4 className="mt-4 font-InterSemiBold text-ft14-17 text-black-10">
                    B. Severe Tread Wear Indicator
                  </h4>
                  <div className="mt-2">
                    <label className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10">
                      <input
                        type="checkbox"
                        className="mt-1"
                        disabled={!edit}
                        checked={tireWorn}
                        onChange={(e) => setTireWorn(e.target.checked)}
                      />
                      Worn to tread wear bars or below
                    </label>
                  </div>
                  <p className="mt-2 font-InterRegular text-ft12-16 text-black-20">
                    Select whether wear bars are flush with or above tread
                    surface anywhere. Observational only.
                  </p>
                </div>
              )}

              {showTreadAndInspection && (
                <div className="mb-4 rounded-md bg-grey-110 px-3 py-2">
                  <h3 className="font-InterSemiBold text-ft14-17 text-black-20">
                    Inspection Tags
                  </h3>
                  <p className="mt-1 font-InterRegular text-ft12-16 text-black-20">
                    (Image-level observational flags – MultiSelect)
                  </p>
                  <div className="mt-2 flex flex-col gap-2">
                    {[
                      "Run Low / Run Flat Damage (Heat Damage)",
                      "Multiple Damages",
                      "Previous Repair",
                      "Loose Tire",
                      "Run Flat Tire (Construction – SSR / ZP / RFT)",
                      "ZR Tire",
                      "Weather Cracking (Weather Checking / Ozone Cracking)"
                    ].map((v) => (
                      <label
                        key={v}
                        className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                      >
                        <input
                          type="checkbox"
                          disabled={!edit}
                          checked={inspectionTags.includes(v)}
                          onChange={() =>
                            setInspectionTags((p) => toggleMulti(p, v))
                          }
                        />
                        {v}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {isEligible && (
                <div className="mb-4 rounded-md bg-grey-110 px-3 py-2">
                  <h3 className="font-InterSemiBold text-ft14-17 text-black-20">
                    Image Visibility & Evidence Quality
                  </h3>
                  <div className="mt-2 flex flex-col gap-2">
                    {[
                      { label: "Clear Visibility", value: "clear" },
                      { label: "Partially Obscured", value: "partial" },
                      {
                        label: "Insufficient Visual Evidence",
                        value: "insufficient"
                      }
                    ].map((o) => (
                      <label
                        key={o.value}
                        className="flex cursor-pointer items-start gap-2 font-InterRegular text-ft14-17 text-black-10"
                      >
                        <input
                          type="radio"
                          name="imageQuality"
                          disabled={!edit}
                          checked={imageQuality === o.value}
                          onChange={() => setImageQuality(o.value)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4 rounded-md bg-grey-110 px-3 py-2">
                <span className="font-InterMedium text-ft14-17 text-black-20">
                  System Derived Condition
                </span>
                <div className="mt-2 rounded bg-grey-90 p-2 font-InterSemiBold text-ft14-17 text-black-20">
                  {airLossConfirmed || "Not Determined"}
                </div>
              </div>

              {validationMsg && (
                <div className="mb-4 text-ft12-16 text-red-10">
                  {validationMsg}
                </div>
              )}
            </div>

            {edit && (
              <div className="flex w-full justify-between pt-5">
                <button
                  disabled={
                    !edit ||
                    !imageEligibility ||
                    (!isPnp && !damageType) ||
                    !hasTagChanges
                  }
                  onClick={ImageTagging}
                  className={`h-8 w-full rounded-[4px] ${
                    !edit ||
                    !imageEligibility ||
                    (!isPnp && !damageType) ||
                    !hasTagChanges
                      ? "bg-green-10 bg-opacity-[.6]"
                      : "bg-green-10"
                  } font-InterSemiBold text-ft12-16 text-offWhite-10`}
                >
                  Submit
                </button>
              </div>
            )}
          </div>
          {(hasLocalData?.modifiedOn ||
            openImage?.allImages[index]?.modifiedOn) && (
            <div>
              <span className="font-InterRegular text-ft12-16 text-black-10">
                Last saved:{" "}
                <b>
                  {" "}
                  {moment(
                    hasLocalData?.modifiedOn ||
                      openImage?.allImages[index]?.modifiedOn
                  ).format("DD MMM YYYY, hh:mm A")}
                </b>
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="flex h-full w-20 flex-col justify-between pb-12 pl-6 pt-6">
        <div className="cursor-pointer">
          <img
            className="w-4 rounded object-cover"
            src="/images/white-cross-icon.svg"
            alt="white-cross-icon"
            onClick={closePreviewModal}
          />
        </div>
        <div className="flex flex-col gap-7">
          <div className="cursor-pointer" onClick={zoomIn}>
            <img
              className="w-4 rounded object-cover"
              src="/images/white-plus-icon.svg"
              alt="white-cross-icon"
            />
          </div>
          <div className="cursor-pointer" onClick={zoomOut}>
            <img
              className="w-4 rounded object-cover"
              src="/images/white-minus-icon.svg"
              alt="white-cross-icon"
            />
          </div>
        </div>
      </div>
      <div className="flex size-full">
        <div className="flex h-full w-3/4 flex-col items-center justify-between gap-8 overflow-y-auto pb-12">
          <div className="relative flex h-85per w-full flex-col items-center gap-8">
            <div
              className={`relative h-full ${
                openImage?.allImages[index]?.image
                  ?.toLowerCase()
                  ?.includes(".pdf") || !openImage?.allImages[index]?.image
                  ? "min-w-50per"
                  : ""
              } w-auto  overflow-auto`}
            >
              <div className="flex size-full items-center justify-center">
                {openImage?.allImages[index]?.image
                  ?.toLowerCase()
                  ?.includes(".pdf") ? (
                  <div className="size-full">
                    <iframe
                      src={openImage?.allImages[index]?.image}
                      width="100%"
                      height="100%"
                    />
                  </div>
                ) : openImage?.allImages[index]?.image ? (
                  <img
                    src={openImage?.allImages[index]?.image}
                    alt="Preview"
                    style={{
                      transform: `scale(${scale})`,
                      transformOrigin: "top left"
                    }}
                    className="size-full transition-transform duration-300"
                  />
                ) : (
                  <div className="flex size-full flex-col  items-center justify-center gap-3 bg-grey-90 text-center">
                    <span className="w-full font-InterRegular text-ft18-21 text-grey-20">
                      No Images attached
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="absolute bottom-5 flex w-full items-center justify-center gap-3.5 bg-transparent">
              <div
                onClick={() => {
                  if (index > 0) setIndex(index - 1);
                }}
                className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-black-0 bg-opacity-[.8]"
              >
                <img src="/images/left-white-arrow.svg" alt="" />
              </div>
              <div
                onClick={() => {
                  if (openImage?.allImages?.length > index + 1)
                    setIndex(index + 1);
                }}
                className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-black-0 bg-opacity-[.8]"
              >
                <img src="/images/right-white-arrow.svg" alt="" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {openImage?.allImages?.map((item, idx) => {
              return (
                <div
                  onClick={() => setIndex(idx)}
                  key={idx}
                  className="size-16 cursor-pointer 
                   rounded-[10px] opacity-60"
                >
                  {item?.image?.toLowerCase()?.includes(".pdf") ? (
                    <img
                      src="/images/document.svg"
                      alt=""
                      className="size-full rounded-[10px]"
                    />
                  ) : item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      className="size-full rounded-[10px]"
                    />
                  ) : (
                    <div className="flex size-full flex-col items-center justify-center gap-3 rounded-lg bg-grey-90 text-center">
                      <img src="/images/image-logo-light.svg" alt="logo" />
                      <span className="w-20 font-InterRegular text-ft12-16 text-grey-20">
                        No Images attached
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="w-[25%] bg-white p-5">
          <div className="font-InterMedium text-ft18-21 text-black-10">
            {Utils.camelToTitleCase(componentName)}
          </div>
          <div className="flex gap-1 pt-5">
            <span className="rounded border border-grey-70 bg-grey-80 p-1 font-InterMedium text-ft14-17 text-black-20">
              {Utils.camelToTitleCase(openImage?.allImages[index]?.type)}
            </span>
          </div>

          {componentData?.length > 0 && (
            <div className="mt-5 flex flex-col gap-3 rounded-md bg-grey-110 p-4">
              {componentData?.map((item, idx) => {
                if (item?.questionText === "") return;
                return (
                  <div key={idx} className="flex justify-between">
                    <span className="w-4/5 font-InterRegular text-ft14-17 text-black-10">
                      {item.questionText}
                    </span>
                    <span className="font-InterMedium text-ft14-17 text-black-10">
                      {item?.answer === "y"
                        ? "Yes"
                        : item?.answer === "n"
                          ? "No"
                          : Utils.formatText(item.answer)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageZoom;
